import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  ShoppingBag, 
  Loader2, 
  RefreshCw,
  TrendingUp,
  Download,
  Calendar,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import {
  purchaseDashboardApiService,
  MonthlySummary,
  MonthlySummaryWithCategorySupplier,
  CategoryDetail,
  SupplierDetail,
} from '../services/purchaseDashboardApi';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Cell
} from 'recharts';

// Theme color for bars - using blue theme
const THEME_COLOR = '#3b82f6'; // blue-500
const THEME_COLOR_SELECTED = '#f97316'; // orange-500 for selected

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function PurchaseDashboard() {
  const { selectedBranch } = useBranch();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [detailedRows, setDetailedRows] = useState<MonthlySummaryWithCategorySupplier[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<{ year: number; month: number } | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('ALL');
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('ALL');

  // Set default to current month and year
  useEffect(() => {
    const now = new Date();
    setSelectedMonth({ year: now.getFullYear(), month: now.getMonth() + 1 });
  }, []);

  // Load monthly summary on mount and when branch changes
  useEffect(() => {
    loadMonthlySummary();
  }, [selectedBranch]);

  const loadMonthlySummary = async () => {
    try {
      setLoading(true);
      const branchId = selectedBranch || user?.defaultBranchId;
      const response = await purchaseDashboardApiService.getMonthlySummaryDetailed(branchId || undefined);
      
      if (response && response.success && Array.isArray(response.data)) {
        setDetailedRows(response.data);
      } else {
        toast.error('Failed to load monthly summary');
        setDetailedRows([]);
      }
    } catch (error) {
      // console.error('Error loading monthly summary:', error);
      toast.error('Failed to load monthly purchase summary');
      setDetailedRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBarClick = (data: any) => {
    if (data) {
      let year: number, month: number;
      
      // Use monthDate if available, otherwise fallback to year and month
      if (data.monthDate) {
        const date = new Date(data.monthDate);
        year = date.getFullYear();
        month = date.getMonth() + 1;
      } else if (data.year && data.month) {
        year = data.year;
        month = data.month;
      } else {
        return;
      }
      
      setSelectedMonth({ year, month });
    }
  };

  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined || isNaN(value)) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined || isNaN(value)) return '0';
    return value.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  // Apply global filters (category/supplier) across ALL months
  const filteredRowsAllMonths = useMemo(() => {
    let rows = detailedRows;
    if (selectedCategoryId !== 'ALL') {
      rows = rows.filter((r) => String((r as any).categoryId ?? '') === String(selectedCategoryId));
    }
    if (selectedSupplierId !== 'ALL') {
      rows = rows.filter((r) => String((r as any).supplierId ?? '') === String(selectedSupplierId));
    }
    return rows;
  }, [detailedRows, selectedCategoryId, selectedSupplierId]);

  // Monthly chart data derived from filtered rows (whole dashboard reacts to filters)
  const monthlySummary: MonthlySummary[] = useMemo(() => {
    const monthlyMap = new Map<string, MonthlySummary>();
    filteredRowsAllMonths.forEach((row) => {
      const year = Number((row as any).year);
      const month = Number((row as any).month);
      if (!year || !month) return;
      const key = `${year}-${month}`;
      const prev = monthlyMap.get(key);
      const addValue = Number((row as any).totalPurchaseValue ?? 0) || 0;
      const addCount = Number((row as any).purchaseCount ?? 0) || 0;

      if (!prev) {
        monthlyMap.set(key, {
          year,
          month,
          monthDate: (row as any).monthDate,
          monthName: (row as any).monthName,
          totalPurchaseValue: addValue,
          purchaseCount: addCount || undefined,
        });
      } else {
        prev.totalPurchaseValue = (prev.totalPurchaseValue || 0) + addValue;
        prev.purchaseCount = (Number(prev.purchaseCount ?? 0) + addCount) || prev.purchaseCount;
      }
    });

    return Array.from(monthlyMap.values())
      .map((item: any) => {
        let displayLabel = '';
        let sortDate: Date;

        if (item.monthDate) {
          sortDate = new Date(item.monthDate);
          const monthName =
            MONTH_NAMES[sortDate.getMonth()] || MONTH_NAMES[item.month - 1] || `M${item.month}`;
          displayLabel = `${monthName} ${sortDate.getFullYear()}`;
        } else {
          sortDate = new Date(item.year, item.month - 1, 1);
          const monthName = MONTH_NAMES[item.month - 1] || `M${item.month}`;
          displayLabel = `${monthName} ${item.year}`;
        }

        return {
          ...item,
          monthName: item.monthName || MONTH_NAMES[item.month - 1] || `M${item.month}`,
          displayLabel,
          sortDate: sortDate.getTime(),
        };
      })
      .sort((a: any, b: any) => (a.sortDate || 0) - (b.sortDate || 0));
  }, [filteredRowsAllMonths]);

  const rowsForSelectedMonth = useMemo(() => {
    if (!selectedMonth) return [];
    return filteredRowsAllMonths.filter(
      (r) => Number((r as any).year) === selectedMonth.year && Number((r as any).month) === selectedMonth.month
    );
  }, [filteredRowsAllMonths, selectedMonth]);

  const categoryDetails: CategoryDetail[] = useMemo(() => {
    const map = new Map<string, CategoryDetail>();
    rowsForSelectedMonth.forEach((r) => {
      const id = (r as any).categoryId;
      const name = (r as any).categoryName ?? 'N/A';
      if (id == null) return;
      const key = String(id);
      const prev = map.get(key);
      const addValue = Number((r as any).totalPurchaseValue ?? 0) || 0;
      const addCount = Number((r as any).purchaseCount ?? 0) || 0;
      if (!prev) {
        map.set(key, {
          categoryId: Number(id),
          categoryName: String(name),
          totalPurchaseValue: addValue,
          purchaseCount: addCount || undefined,
        });
      } else {
        prev.totalPurchaseValue = (prev.totalPurchaseValue || 0) + addValue;
        prev.purchaseCount = (Number(prev.purchaseCount ?? 0) + addCount) || prev.purchaseCount;
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.categoryName ?? '').localeCompare(String(b.categoryName ?? ''), undefined, { sensitivity: 'base' })
    );
  }, [rowsForSelectedMonth]);

  const supplierDetails: (SupplierDetail & {
    deliveryRating?: number;
    onTimeDeliveryPercentage?: number;
    performanceScore?: number;
  })[] = useMemo(() => {
    const map = new Map<string, SupplierDetail>();
    rowsForSelectedMonth.forEach((r) => {
      const id = (r as any).supplierId;
      const name = (r as any).supplierName ?? 'N/A';
      if (id == null) return;
      const key = String(id);
      const prev = map.get(key);
      const addValue = Number((r as any).totalPurchaseValue ?? 0) || 0;
      const addCount = Number((r as any).purchaseCount ?? 0) || 0;
      if (!prev) {
        map.set(key, {
          supplierId: Number(id),
          supplierName: String(name),
          totalPurchaseValue: addValue,
          purchaseCount: addCount || undefined,
        });
      } else {
        prev.totalPurchaseValue = (prev.totalPurchaseValue || 0) + addValue;
        prev.purchaseCount = (Number(prev.purchaseCount ?? 0) + addCount) || prev.purchaseCount;
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.supplierName ?? '').localeCompare(String(b.supplierName ?? ''), undefined, { sensitivity: 'base' })
    );
  }, [rowsForSelectedMonth]);

  const categoryTotal = useMemo(() => {
    return categoryDetails.reduce((sum, item) => sum + (item.totalPurchaseValue || 0), 0);
  }, [categoryDetails]);

  const supplierTotal = useMemo(() => {
    return supplierDetails.reduce((sum, item) => sum + (item.totalPurchaseValue || 0), 0);
  }, [supplierDetails]);

  // Get top 15 suppliers sorted by purchase value (after applying global filters)
  const topSuppliers = useMemo(() => {
    return [...supplierDetails]
      .sort((a, b) => (b.totalPurchaseValue || 0) - (a.totalPurchaseValue || 0))
      .slice(0, 15);
  }, [supplierDetails]);

  const categoryOptions = useMemo(() => {
    const map = new Map<string, { categoryId: number; categoryName: string }>();
    const baseRows =
      selectedSupplierId !== 'ALL'
        ? detailedRows.filter((r) => String((r as any).supplierId ?? '') === String(selectedSupplierId))
        : detailedRows;
    baseRows.forEach((r) => {
      const id = (r as any).categoryId;
      if (id == null) return;
      const key = String(id);
      if (!map.has(key)) {
        map.set(key, {
          categoryId: Number(id),
          categoryName: String((r as any).categoryName ?? 'N/A'),
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.categoryName ?? '').localeCompare(String(b.categoryName ?? ''), undefined, { sensitivity: 'base' })
    );
  }, [detailedRows, selectedSupplierId]);

  const supplierOptions = useMemo(() => {
    const map = new Map<string, { supplierId: number; supplierName: string }>();
    const baseRows =
      selectedCategoryId !== 'ALL'
        ? detailedRows.filter((r) => String((r as any).categoryId ?? '') === String(selectedCategoryId))
        : detailedRows;
    baseRows.forEach((r) => {
      const id = (r as any).supplierId;
      if (id == null) return;
      const key = String(id);
      if (!map.has(key)) {
        map.set(key, {
          supplierId: Number(id),
          supplierName: String((r as any).supplierName ?? 'N/A'),
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.supplierName ?? '').localeCompare(String(b.supplierName ?? ''), undefined, { sensitivity: 'base' })
    );
  }, [detailedRows, selectedCategoryId]);

  // Determine whether to show values in Lakhs or Crores on Y axis
  const yAxisTickFormatter = useMemo(() => {
    const maxValue = monthlySummary.reduce(
      (max, item) => Math.max(max, item.totalPurchaseValue || 0),
      0
    );
    // Switch to Cr when value exceeds ₹100L
    const useCrores = maxValue >= 10_000_000;

    return (value: number) => {
      if (useCrores) {
        return `₹${(value / 10_000_000).toFixed(1)}Cr`;
      }
      return `₹${(value / 100_000).toFixed(1)}L`;
    };
  }, [monthlySummary]);

  // Custom tooltip for bar chart
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{data.displayLabel || `${data.monthName} ${data.year}`}</p>
          <p className="text-primary-600">
            Total: {formatCurrency(data.totalPurchaseValue)}
          </p>
          {data.purchaseCount !== undefined && (
            <p className="text-gray-600 text-sm">Orders: {data.purchaseCount}</p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-primary-200 flex items-center justify-center">
                <FileText className="h-5 w-5 text-primary-700" />
              </div>
              <div>
                <CardTitle className="text-xl text-gray-900">Purchase Dashboard</CardTitle>
                <p className="text-gray-600 text-sm">Monthly purchase analysis and insights</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={loadMonthlySummary}
                variant="outline"
                size="sm"
                disabled={loading}
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Global filters (apply to details tables) */}
          <div className="flex flex-col gap-3 mb-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary-600" />
              <div>
                <p className="text-sm text-gray-600">Period</p>
                <p className="font-semibold text-gray-800">
                  {selectedMonth
                    ? `${MONTH_NAMES[selectedMonth.month - 1]} ${selectedMonth.year}`
                    : 'Select a month from the chart'}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 whitespace-nowrap">Category</span>
                <Select
                  value={selectedCategoryId}
                  onValueChange={setSelectedCategoryId}
                  disabled={loading || detailedRows.length === 0}
                >
                  <SelectTrigger className="w-[240px] bg-white">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContentWithSearch searchPlaceholder="Search categories..." debounceMs={150}>
                    <SelectItem value="ALL">All categories</SelectItem>
                    {categoryOptions.map((c) => (
                      <SelectItem key={c.categoryId} value={String(c.categoryId)}>
                        {c.categoryName || 'N/A'}
                      </SelectItem>
                    ))}
                  </SelectContentWithSearch>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 whitespace-nowrap">Supplier</span>
                <Select
                  value={selectedSupplierId}
                  onValueChange={setSelectedSupplierId}
                  disabled={loading || detailedRows.length === 0}
                >
                  <SelectTrigger className="w-[260px] bg-white">
                    <SelectValue placeholder="All suppliers" />
                  </SelectTrigger>
                  <SelectContentWithSearch searchPlaceholder="Search suppliers..." debounceMs={150}>
                    <SelectItem value="ALL">All suppliers</SelectItem>
                    {supplierOptions.map((s) => (
                      <SelectItem key={s.supplierId} value={String(s.supplierId)}>
                        {s.supplierName || 'N/A'}
                      </SelectItem>
                    ))}
                  </SelectContentWithSearch>
                </Select>
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
                disabled={loading || detailedRows.length === 0}
                onClick={() => {
                  setSelectedCategoryId('ALL');
                  setSelectedSupplierId('ALL');
                }}
              >
                Clear
              </Button>
            </div>
          </div>

          {/* Monthly Summary Bar Chart */}
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary-600" />
              <p className="text-gray-600">Loading monthly purchase summary...</p>
            </div>
          ) : monthlySummary.length > 0 ? (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary-600" />
                Monthly Purchase Summary (Last 12 Months)
              </h3>
              <div className="bg-white p-4 rounded-lg border border-primary-200">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={monthlySummary}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="displayLabel" 
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis 
                      tickFormatter={yAxisTickFormatter}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar 
                      dataKey="totalPurchaseValue" 
                      onClick={handleBarClick}
                      style={{ cursor: 'pointer' }}
                    >
                      {monthlySummary.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={
                            selectedMonth && 
                            entry.year === selectedMonth.year && 
                            entry.month === selectedMonth.month
                              ? THEME_COLOR_SELECTED
                              : THEME_COLOR
                          } 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Click on a bar to view detailed breakdown by category and supplier
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-lg border border-primary-200">
              <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No purchase data available</p>
              <p className="text-gray-400 text-sm mt-2">
                No purchase data found for the selected period
              </p>
            </div>
          )}

          {/* Category and Supplier Details Tables */}
          {selectedMonth && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800">Breakdown</h3>

              {loading ? (
                <div className="p-8 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary-600" />
                  <p className="text-gray-600">Loading purchase details...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Category Details Table */}
                  <Card className="border border-primary-200">
                    <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">By Category</CardTitle>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Total</p>
                          <p className="text-lg font-semibold text-primary-600">{formatCurrency(categoryTotal)}</p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      {categoryDetails.length > 0 ? (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-gray-50">
                                <TableHead>Category</TableHead>
                                <TableHead className="text-right">Purchase Value</TableHead>
                                <TableHead className="text-right">Percentage</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {categoryDetails.map((item, index) => {
                                const percentage = categoryTotal > 0 
                                  ? ((item.totalPurchaseValue / categoryTotal) * 100).toFixed(1)
                                  : '0.0';
                                return (
                                  <TableRow key={item.categoryId || index} className="hover:bg-primary-50/50">
                                    <TableCell className="font-medium">
                                      {item.categoryName || 'N/A'}
                                    </TableCell>
                                    <TableCell className="text-right font-semibold text-primary-600">
                                      {formatCurrency(item.totalPurchaseValue)}
                                    </TableCell>
                                    <TableCell className="text-right text-gray-600">
                                      {percentage}%
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <div className="p-8 text-center text-gray-500">
                          No category data for this filter
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Supplier Details Table */}
                  <Card className="border border-primary-200">
                    <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">By Supplier (Top 15)</CardTitle>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Total</p>
                          <p className="text-lg font-semibold text-primary-600">{formatCurrency(supplierTotal)}</p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      {topSuppliers.length > 0 ? (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-gray-50">
                                <TableHead>Supplier</TableHead>
                                <TableHead className="text-right">Purchase Value</TableHead>
                                <TableHead className="text-right">Percentage</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {topSuppliers.map((item, index) => {
                                const percentage =
                                  supplierTotal > 0
                                    ? ((item.totalPurchaseValue / supplierTotal) * 100).toFixed(1)
                                    : '0.0';
                                
                                return (
                                  <TableRow key={item.supplierId || index} className="hover:bg-primary-50/50">
                                    <TableCell className="font-medium">
                                      {item.supplierName || 'N/A'}
                                    </TableCell>
                                    <TableCell className="text-right font-semibold text-primary-600">
                                      {formatCurrency(item.totalPurchaseValue)}
                                    </TableCell>
                                    <TableCell className="text-right text-gray-600">{percentage}%</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <div className="p-8 text-center text-gray-500">
                          No supplier data available for this month
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

